// Copyright (c) Bentley Systems
import {
  Badge,
  Button,
  DefaultCell,
  ExpandableBlock,
  ProgressRadial,
  Table,
  Text,
  ToggleSwitch,
} from "@itwin/itwinui-react";
import { useCallback, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAccessToken } from "../app/authorization-slice";
import {
  selectMainMenuHeightWithMargin,
  selectSideMenuWidthWithMargin,
} from "../app/header-slice";
import { setConfirmation } from "../app/confirmation-slice";
import { DawComponent } from "../Helpers/daw-component";
import { useNodePermission } from "../Helpers/useNodePermission";
import { useElementHeights } from "../Helpers/useElementHeights";
import { useToastNotificationService } from "../Helpers/ToastNotificationService";
import { useOnPremRoutingApi } from "./useOnPremRoutingApi";
import {
  computeITwinStatus,
  ITwinRoutingOverview,
  MqttClientOverview,
  OnPremUsageRecord,
} from "./Models/on-prem-routing-models";
import { CellRendererProps, Column, Row } from "@itwin/itwinui-react/react-table";

const OnPremRouting = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToastNotificationService();
  const accessToken = useAppSelector(selectAccessToken);
  const sideMenuWidth = useAppSelector(selectSideMenuWidthWithMargin);
  const mainMenuHeight = useAppSelector(selectMainMenuHeightWithMargin);

  const { isNodeAllowed, isLoading: isPermissionLoading } = useNodePermission(
    DawComponent.DAW_COMPONENT_ON_PREM_ROUTING,
  );

  const { isNodeAllowed: isToggleAllowed } = useNodePermission(
    DawComponent.DAW_COMPONENT_ON_PREM_ROUTING_TOGGLE,
    { enabled: isNodeAllowed, silent: true },
  );

  const { iTwins, isLoading, isToggling, fetchOverview, toggleMqttClient } =
    useOnPremRoutingApi({ accessToken, dispatch, isNodeAllowed });

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight] = useElementHeights(headerRef);

  const allHeadersHeight = mainMenuHeight + headerHeight;

  const onToggleClick = useCallback(
    (client: MqttClientOverview) => {
      const action = client.isUpgraded ? "route back to Legacy" : "mark as ready for Isolated";
      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          caption: "Confirm routing change",
          text: `Are you sure you want to ${action} MQTT client "${client.mqttClientName}"?`,
          okText: "Confirm",
          cancelText: "Cancel",
          onOkAction: async () => {
            const ok = await toggleMqttClient(client.mqttClientName);
            if (ok) {
              showToast({
                status: "positive",
                message: `MQTT client "${client.mqttClientName}" updated successfully.`,
              });
            }
          },
        }),
      );
    },
    [dispatch, toggleMqttClient, showToast],
  );

  // ── Usage records columns (innermost level) ─────────────────────────────
  const usageColumns: Column<OnPremUsageRecord>[] = useMemo(
    () => [
      {
        Header: "System",
        accessor: (r: OnPremUsageRecord) => r.systemId,
        id: "systemId",
        width: 200,
      },
      {
        Header: "Project",
        accessor: (r: OnPremUsageRecord) => r.projectName,
        id: "projectName",
        minWidth: 150,
      },
      {
        Header: "Times used",
        accessor: (r: OnPremUsageRecord) => r.timesUsed,
        id: "timesUsed",
        width: 110,
      },
      {
        Header: "Last used",
        accessor: (r: OnPremUsageRecord) =>
          r.lastTimeUsed
            ? new Date(r.lastTimeUsed).toLocaleString()
            : "—",
        id: "lastTimeUsed",
        width: 180,
      },
    ],
    [],
  );

  const mqttColumns: Column<MqttClientOverview>[] = useMemo(
    () => [
      {
        Header: "MQTT Client",
        accessor: (c: MqttClientOverview) => c.mqttClientName,
        id: "mqttClientName",
        minWidth: 200,
      },
      {
        Header: "Routing",
        id: "routing",
        width: 120,
        cellRenderer: (props: CellRendererProps<MqttClientOverview>) => {
          const client = props.cellProps.row.original;
          return (
            <DefaultCell {...props}>
              <Badge backgroundColor={client.isUpgraded ? "positive" : "warning"}>
                {client.isUpgraded ? "Isolated" : "Legacy"}
              </Badge>
            </DefaultCell>
          );
        },
      },
      {
        Header: "Isolated?",
        id: "toggle",
        width: 100,
        disableSortBy: true,
        cellRenderer: (props: CellRendererProps<MqttClientOverview>) => {
          const client = props.cellProps.row.original;
          const toggling = isToggling === client.mqttClientName;
          return (
            <DefaultCell {...props} style={{ justifyContent: "center", whiteSpace: "nowrap" }}>
              {toggling ? (
                <ProgressRadial size="small" />
              ) : (
                <ToggleSwitch
                  checked={client.isUpgraded}
                  onChange={() => onToggleClick(client)}
                  disabled={!isToggleAllowed}
                  label={client.isUpgraded ? "Isolated" : "Legacy"}
                />
              )}
            </DefaultCell>
          );
        },
      },
      {
        Header: "Upgraded by",
        accessor: (c: MqttClientOverview) => c.upgradedByName ?? "—",
        id: "upgradedByName",
        width: 160,
      },
      {
        Header: "Upgraded at",
        accessor: (c: MqttClientOverview) =>
          c.upgradedAt ? new Date(c.upgradedAt).toLocaleString() : "—",
        id: "upgradedAt",
        width: 180,
      },
    ],
    [isToggling, isToggleAllowed, onToggleClick],
  );

  const expandables = useMemo(
    () =>
      iTwins.map((iTwin: ITwinRoutingOverview) => {
        const status = computeITwinStatus(iTwin);
        return (
          <ExpandableBlock.Wrapper key={iTwin.iTwinId}>
            <ExpandableBlock.Trigger>
              <ExpandableBlock.ExpandIcon />
              <ExpandableBlock.LabelArea>
                <ExpandableBlock.Title>
                  <Text>
                    <b>{iTwin.displayName ? `${iTwin.displayName} (${iTwin.iTwinId})` : iTwin.iTwinId}</b>
                  </Text>
                </ExpandableBlock.Title>
                <ExpandableBlock.Caption>
                  <div className="container">
                    <div className="column left">
                      Routing: {" "}
                      <Badge backgroundColor={status === "Isolated" ? "positive" : "warning"}>
                        {status}
                      </Badge>
                    </div>
                    <div className="column center">
                      MQTT Clients: <b>{iTwin.mqttClients.length}</b>
                    </div>
                    <div className="column right" />
                  </div>
                </ExpandableBlock.Caption>
              </ExpandableBlock.LabelArea>
            </ExpandableBlock.Trigger>
            <ExpandableBlock.Content>
              <div style={{ padding: "8px 16px 8px 32px" }}>
                <Table
                  columns={mqttColumns}
                  data={iTwin.mqttClients}
                  emptyTableContent="No MQTT clients."
                  density="extra-condensed"
                  styleType="zebra-rows"
                  subComponent={(clientRow: Row<MqttClientOverview>) => (
                    <div style={{ padding: "8px 16px 8px 48px", width: "100%", boxSizing: "border-box" }}>
                      <Text variant="small" isMuted style={{ marginBottom: "4px" }}>
                        Usage records
                      </Text>
                      <Table
                        columns={usageColumns}
                        data={clientRow.original.usages}
                        emptyTableContent="No usage records."
                        density="extra-condensed"
                        styleType="zebra-rows"
                        style={{ width: "100%" }}
                      />
                    </div>
                  )}
                />
              </div>
            </ExpandableBlock.Content>
          </ExpandableBlock.Wrapper>
        );
      }),
    [iTwins, mqttColumns, usageColumns],
  );

  if (isPermissionLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", paddingTop: "48px" }}>
        <ProgressRadial />
      </div>
    );
  }

  if (!isNodeAllowed) {
    return (
      <div style={{ padding: "24px" }}>
        <Text>You do not have permission to access On-Prem Routing.</Text>
      </div>
    );
  }

  return (
    <div style={{ height: "100%" }}>
      <div className="becs-infopanelbody-footer-line becs-all-margin" ref={headerRef}>
        <div className="rowWrap" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Text variant="title">On-Prem Routing</Text>
          <Button
            styleType="default"
            size="small"
            onClick={() => { void fetchOverview(); }}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className="becs-ltr-margin">
        <div
          style={{
            height: `calc(100vh - ${allHeadersHeight}px)`,
            width: `calc(100vw - ${sideMenuWidth}px)`,
            background: "var(--iui-color-background)",
          }}
        >
          {isLoading ? (
            <div
              className="becs-ltr-margin"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                paddingTop: "24px",
              }}
            >
              <ProgressRadial indeterminate />
            </div>
          ) : iTwins.length === 0 ? (
            <div className="becs-ltr-margin" style={{ paddingTop: "16px" }}>
              <Text>No on-prem routing data found.</Text>
            </div>
          ) : (
            expandables
          )}
        </div>
      </div>
    </div>
  );
};

export default OnPremRouting;
